/* global React */

// ─────────────────────────────────────────────────────────────
// TOSY Splash — fully themeable by `palette` prop
// palette = { bg, accent, accent2, accent3, ink (wordmark), mono, subtle }
// ─────────────────────────────────────────────────────────────

// Palette lifted from the real TOSY sign: sky blue field, hot pink + yellow lettering.
const DEFAULT_PALETTE = {
  bg: '#070d1c',
  accent: '#ff2e88',     // sign pink
  accent2: '#2e9bff',    // sign blue
  accent3: '#ffd23f',    // sign yellow
  ink: '#ffffff',
  mono: 'rgba(226,238,255,0.55)',
  subtle: 'rgba(226,238,255,0.8)',
  ballCore: '#fff',
  ballMid: '#9fd0ff',
  ballEdge: '#0d2144',
};

function hexToRgba(hex, a = 1) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}



// Reflected light thrown off the ball and across the room.
// A ball spinning on a vertical axis sweeps its reflections
// HORIZONTALLY across the walls, all travelling the same way at a
// steady rate — not orbiting the ball like a pinwheel. Dots are
// small, hard-edged squares (the facets are square), and they dim
// as they cross the darker parts of the room.
function LightField({ palette: P, uid }) {
  // 40, not 64: every dot animates, so every dot is its own compositor layer.
  // Glow comes from box-shadow rather than filter: blur() — same soft halo
  // around a hard square core, without a per-frame filter pass each.
  const dots = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < 40; i++) {
      const a = Math.abs(Math.sin(i * 45.233) * 43758.5453) % 1;
      const b = Math.abs(Math.sin(i * 12.9898) * 24634.6345) % 1;
      const c = Math.abs(Math.sin(i * 78.233) * 12938.1234) % 1;
      out.push({
        id: i,
        top: 4 + b * 92,             // spread down the whole screen
        size: 3 + a * 9,
        hue: 208 + a * 78,           // steel blue → violet only
        dur: 13 + c * 11,            // similar speeds — same ball, same spin
        delay: -(c * 24),
        glow: 3 + a * 7,
        op: 0.3 + b * 0.45,
        drift: (a - 0.5) * 26,       // slight vertical drift as it crosses
        rev: i % 5 === 0,            // a few bounce off the back wall
      });
    }
    return out;
  }, []);

  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
      {dots.map(d => (
        <div key={d.id} style={{
          position: 'absolute', top: d.top + '%', left: 0,
          width: d.size, height: d.size, borderRadius: 1.5,
          background: 'hsl(' + d.hue.toFixed(0) + ',92%,72%)',
          boxShadow: '0 0 ' + d.glow.toFixed(1) + 'px hsl(' + d.hue.toFixed(0) + ',95%,68%), 0 0 ' + (d.glow * 2.4).toFixed(1) + 'px hsl(' + d.hue.toFixed(0) + ',95%,64%)',
          opacity: d.op,
          animation: 'lf-sweep-' + uid + (d.rev ? '-r' : '') + ' ' + d.dur + 's linear infinite, lf-flick-' + uid + ' ' + (2.6 + (d.id % 6) * 0.5) + 's ease-in-out infinite',
          animationDelay: d.delay + 's, ' + (-d.id * 0.28) + 's',
          ['--drift']: d.drift + 'px',
        }} />
      ))}
      <style>{`
        @keyframes lf-sweep-${uid} {
          from { transform: translateX(-14vw) translateY(0); }
          to   { transform: translateX(114vw) translateY(var(--drift, 0px)); }
        }
        @keyframes lf-sweep-${uid}-r {
          from { transform: translateX(114vw) translateY(0); }
          to   { transform: translateX(-14vw) translateY(var(--drift, 0px)); }
        }
        @keyframes lf-flick-${uid} { 0%,100% { opacity: 0.35; } 40% { opacity: 1; } 58% { opacity: 0.5; } }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MirrorBall — a real ball is not chrome. It is hundreds of tiny
// facets each reflecting a different coloured light in the room,
// so it reads as a saturated rainbow grid with white hotspots.
// Density + saturation are what sell it.
// ─────────────────────────────────────────────────────────────
// Drawn on a canvas, not in the DOM. The facet maths below is unchanged from
// the original — same rings, same hues, same key light — but the 1,708 facets
// used to be 1,708 absolutely-positioned divs inside a spinning preserve-3d
// container. That cost ~1,700 node creations on splash mount, then a full
// 3D re-composite of every one of them each frame, for facets barely 3px tall.
// As a canvas it is a single element and ~850 fillRects a frame (back-facing
// facets are skipped, exactly as backface-visibility:hidden used to).
function MirrorBall({ palette: P, uid = 'mb', size = 108 }) {
  const R = size / 2;
  const rings = 41;
  const canvasRef = React.useRef(null);

  // The ball legitimately draws outside its own radius: perspective scales the
  // near facets up to ~1.15x, every facet rect has a width of its own, and the
  // hotspot pass draws rects 2.1x larger again. A canvas sized exactly to the
  // diameter clipped all of that flat and squared the silhouette off — it
  // stopped reading as a ball. The old DOM version never showed this because
  // its 3D-transformed divs were free to overflow the container.
  // The canvas is inset by -PAD so the ball still sits exactly where it did.
  // Proportional, so it still holds if the ball is ever rendered at another size.
  const PAD = Math.ceil(size * 0.12);
  const CANVAS = size + PAD * 2;

  const tiles = React.useMemo(() => {
    const out = [];
    for (let r = 0; r < rings; r++) {
      const phi = -83 + (166 * r) / (rings - 1);
      const rad = (phi * Math.PI) / 180;
      const ringR = Math.cos(rad) * R;
      const y = Math.sin(rad) * R;
      const n = Math.max(10, Math.round(Math.cos(rad) * 62));
      for (let c = 0; c < n; c++) {
        const theta = (360 * c) / n;
        const hash = Math.abs(Math.sin(r * 12.9898 + c * 78.233) * 43758.5453) % 1;
        const nx = Math.sin((theta * Math.PI) / 180) * Math.cos(rad);
        const ny = Math.sin(rad);
        // key light upper-left
        const key = Math.max(0, 1 - Math.hypot(nx + 0.5, ny + 0.5) / 1.75);
        // each facet catches a different room light — hue drifts across the sphere
        // 210°–290° only: steel blue through to violet. Never warm.
        const hue = 210 + ((nx * 0.5 + 0.5) * 46) + ((ny * 0.5 + 0.5) * 22) + hash * 12;
        const hot = key > 0.74 && hash > 0.35;
        // colour is baked once here rather than recomputed every frame
        let color;
        if (hot) color = '#ffffff';
        else if (key > 0.6 && hash > 0.72) color = '#f2f8ff';
        else {
          const light = 30 + key * 50 + hash * 18;
          const sat = 12 + key * 46 + hash * 20;
          color = 'hsl(' + hue.toFixed(0) + ',' + sat.toFixed(0) + '%,' + light.toFixed(0) + '%)';
        }
        out.push({
          thetaRad: (theta * Math.PI) / 180, y, ringR, color, hot,
          glint: hash > 0.94, phase: hash * Math.PI * 2,
          w: Math.max(0.9, (2 * Math.PI * ringR) / n - 0.22),
          hh: Math.max(0.9, (R * 2 * 1.18) / rings - 0.22),
        });
      }
    }
    return out;
  }, [R]);

  React.useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');

    // Backing store follows the device pixel ratio, and re-follows it if that
    // changes (rotation, zoom, dragged to another display) — sized once at
    // mount, the ball would just go soft. setTransform, not scale, so
    // reapplying it doesn't compound.
    let dpr = 0;
    const fit = () => {
      const next = Math.min(window.devicePixelRatio || 1, 2);
      if (next === dpr) return;
      dpr = next;
      cv.width = Math.round(CANVAS * dpr);
      cv.height = Math.round(CANVAS * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    window.addEventListener('resize', fit);

    const persp = size * 3.8;   // matches the old CSS `perspective`
    const cx = CANVAS / 2, cy = CANVAS / 2;
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0, t0 = null, live = true;
    const draw = ts => {
      if (t0 === null) t0 = ts;
      const el = (ts - t0) / 1000;
      const rot = reduce ? 0 : (el / 20) * Math.PI * 2;   // 20s per revolution, as before
      ctx.clearRect(0, 0, CANVAS, CANVAS);

      // main pass — front-facing facets only
      for (let k = 0; k < tiles.length; k++) {
        const t = tiles[k];
        const a = t.thetaRad + rot;
        const z = Math.cos(a) * t.ringR;
        if (z <= 0) continue;                       // was backface-visibility: hidden
        const s = persp / (persp - z);              // perspective foreshortening
        const w = t.w * s, hh = t.hh * s;
        ctx.globalAlpha = t.glint && !reduce ? 0.5 + 0.5 * Math.abs(Math.sin(el * Math.PI / 2 + t.phase)) : 1;
        ctx.fillStyle = t.color;
        ctx.fillRect(cx + Math.sin(a) * t.ringR * s - w / 2, cy + t.y * s - hh / 2, w, hh);
      }

      // hotspot bloom — additive, cheap stand-in for the old per-tile box-shadow
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.34;
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      for (let k = 0; k < tiles.length; k++) {
        const t = tiles[k];
        if (!t.hot) continue;
        const a = t.thetaRad + rot;
        const z = Math.cos(a) * t.ringR;
        if (z <= 0) continue;
        const s = persp / (persp - z);
        const w = t.w * s * 2.1, hh = t.hh * s * 2.1;
        ctx.fillRect(cx + Math.sin(a) * t.ringR * s - w / 2, cy + t.y * s - hh / 2, w, hh);
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      if (live && !reduce) raf = requestAnimationFrame(draw);
    };
    // Paint one frame straight away rather than waiting on rAF — a backgrounded
    // or throttled tab (and reduced-motion) would otherwise leave the ball blank.
    // draw() schedules its own successor, so this also starts the loop.
    draw(typeof performance !== 'undefined' ? performance.now() : 0);
    return () => { live = false; cancelAnimationFrame(raf); window.removeEventListener('resize', fit); };
  }, [tiles, size, CANVAS]);

  return (
    <div style={{ position: 'relative', width: size + 8, height: size + 34, margin: '16px auto 0', zIndex: 5 }}>
      {/* halo */}
      <div style={{
        position: 'absolute', left: '50%', top: 26 + R, width: size * 2.5, height: size * 2.5,
        transform: 'translate(-50%,-50%)', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(150,130,255,0.3) 0%, rgba(90,150,255,0.14) 44%, transparent 70%)',
      }} />

      {/* cable + mount */}
      <div style={{ position: 'absolute', left: '50%', top: 0, width: 1.5, height: 24, background: 'rgba(255,255,255,0.4)', transform: 'translateX(-50%)' }} />
      <div style={{ position: 'absolute', left: '50%', top: 0, width: 15, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.3)', transform: 'translateX(-50%)' }} />
      <div style={{ position: 'absolute', left: '50%', top: 21, width: 6, height: 6, borderRadius: '50%', background: '#c9b4e8', transform: 'translateX(-50%)' }} />

      <div style={{ position: 'absolute', left: '50%', top: 26, width: size, height: size, transform: 'translateX(-50%)' }}>
        <div style={{ position: 'absolute', inset: 2, borderRadius: '50%', background: '#0d1226' }} />
        <canvas ref={canvasRef} aria-hidden="true" style={{ position: 'absolute', left: -PAD, top: -PAD, width: CANVAS, height: CANVAS, pointerEvents: 'none' }} />
        {/* soft bloom + contact shadow */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle at 32% 26%, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.06) 30%, transparent 52%), radial-gradient(circle at 76% 86%, rgba(6,10,32,0.62) 0%, transparent 60%)',
        }} />
      </div>

      {/* rays */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
        <div key={i} style={{
          position: 'absolute', left: '50%', top: 26 + R, width: 2, height: 170,
          background: 'linear-gradient(to bottom, hsl(' + (212 + i * 11) + ',88%,70%)bb, transparent 76%)',
          transformOrigin: 'top center', filter: 'blur(3px)', opacity: 0.42, pointerEvents: 'none',
          animation: 'mb-ray-' + uid + ' 20s linear infinite',
          animationDelay: (-i * 2.5) + 's',
        }} />
      ))}

      <style>{`
        @keyframes mb-ray-${uid} { from { transform: translateX(-50%) rotate(0deg); } to { transform: translateX(-50%) rotate(360deg); } }
      `}</style>
    </div>
  );
}

function Splash({ onEnter, palette = DEFAULT_PALETTE, uid = 'p0' }) {
  const P = { ...DEFAULT_PALETTE, ...palette };
  const [stage, setStage] = React.useState(0);
  const [tagIdx, setTagIdx] = React.useState(0);
  const tags = [
    '5 Private Rooms',
    'Karaoke from £7.50pp',
    'Open late Thu–Sat',
    'Happy Hour ’til 7pm',
    '5 min from London Bridge',
  ];

  React.useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 1200);
    const t3 = setTimeout(() => setStage(3), 1900);
    return () => { [t1,t2,t3].forEach(clearTimeout); };
  }, []);

  React.useEffect(() => {
    const t = setInterval(() => setTagIdx(i => (i + 1) % tags.length), 2400);
    return () => clearInterval(t);
  }, []);

  // Unique animation names per instance to isolate neon colors
  const flickerName = `neon-flicker-${uid}`;
  const bubbleName = `bubble-rise-${uid}`;

  return (
    <div style={{
      position: 'absolute', inset: 0, background: P.bg,
      overflow: 'hidden', color: P.subtle,
      display: 'flex', flexDirection: 'column',
    }}>
      <style>{`
        @keyframes ${bubbleName} { 0% { transform: translateY(0) scale(1); opacity: 0; } 10% { opacity: 0.7; } 90% { opacity: 0.5; } 100% { transform: translateY(-110vh) scale(0.6); opacity: 0; } }
        @keyframes disco-spin { to { transform: rotate(360deg); } }
        @keyframes eq-bar { 0%,100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
        @keyframes beam-sweep { 0%,100% { transform: rotate(-18deg); opacity: 0.35; } 50% { transform: rotate(18deg); opacity: 0.6; } }
        @keyframes ${flickerName} {
          0%,100% { opacity: 1; text-shadow: 0 0 12px ${P.accent}, 0 0 28px ${P.accent}, 0 0 60px ${P.accent}; }
          50% { opacity: 0.95; text-shadow: 0 0 8px ${P.accent}, 0 0 20px ${P.accent}, 0 0 45px ${P.accent}; }
          52% { opacity: 0.6; }
          54% { opacity: 1; }
        }
        @keyframes fade-slide { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ticker-splash { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>

      {/* stage beams */}
      <div style={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 4, height: '120%', background: `linear-gradient(to bottom, ${hexToRgba(P.accent, 0.6)}, transparent)`, filter: 'blur(20px)', transformOrigin: 'top center', animation: 'beam-sweep 6s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 4, height: '120%', background: `linear-gradient(to bottom, ${hexToRgba(P.accent2, 0.5)}, transparent)`, filter: 'blur(22px)', transformOrigin: 'top center', animation: 'beam-sweep 6s ease-in-out infinite reverse', animationDelay: '1s' }} />
      <div style={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 4, height: '120%', background: `linear-gradient(to bottom, ${hexToRgba(P.accent3, 0.45)}, transparent)`, filter: 'blur(18px)', transformOrigin: 'top center', animation: 'beam-sweep 7s ease-in-out infinite', animationDelay: '2s' }} />

      <LightField palette={P} uid={uid} />

      {/* bubbles */}
      {Array.from({ length: 12 }).map((_, i) => {
        const size = 8 + (i * 3) % 18;
        return (
          <div key={i} style={{
            position: 'absolute', bottom: -30,
            left: `${(i * 8 + 4) % 95}%`,
            width: size, height: size, borderRadius: '50%',
            background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.7), ${hexToRgba(P.accent, 0.25)} 40%, transparent 70%)`,
            boxShadow: `0 0 10px ${hexToRgba(P.accent, 0.4)}`,
            animation: `${bubbleName} ${8 + i % 6}s linear ${i * 0.6}s infinite`,
          }} />
        );
      })}

      {/* faint exterior photo behind everything */}
      {window.TOSY_IMG && (
        <img src={window.TOSY_IMG.exterior} alt="" style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
          opacity: 0.16, filter: 'saturate(1.3) contrast(1.1)', pointerEvents: 'none',
        }} />
      )}
      {/* vignette */}
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, transparent 25%, ${hexToRgba(P.bg, 0.92)} 100%)`, pointerEvents: 'none' }} />
      <div className="grain" style={{ opacity: 0.08 }} />

      {/* top bar */}
      <div style={{ padding: '66px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 5 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: P.mono }}>LONDON · SE1</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: P.mono }}>OPEN LATE</span>
      </div>

      {/* mirror ball */}
      <MirrorBall palette={P} uid={uid} />

      {/* neon wordmark */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 24px', position: 'relative', zIndex: 5 }}>
        <div style={{
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.45em',
          color: P.accent, marginBottom: 10,
          opacity: stage >= 1 ? 1 : 0, transition: 'opacity 0.8s ease',
        }}>
          — TOSY —
        </div>

        <div style={{
          fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 700,
          fontSize: 54, lineHeight: 0.9, letterSpacing: '-0.02em',
          color: P.ink, textAlign: 'center',
          animation: stage >= 1 ? `${flickerName} 3.5s ease-in-out infinite` : 'none',
          opacity: stage >= 1 ? 1 : 0, transition: 'opacity 0.8s ease',
        }}>
          The Old<br/>School Yard
        </div>

        <div style={{
          fontFamily: 'var(--sans)', fontSize: 13, color: P.subtle,
          marginTop: 14, letterSpacing: '0.1em', textTransform: 'uppercase',
          opacity: stage >= 2 ? 1 : 0, transition: 'opacity 0.8s ease',
          height: 16,
        }}>
          <span key={tagIdx} style={{ display: 'inline-block', animation: 'fade-slide 0.4s ease' }}>
            {tags[tagIdx]}
          </span>
        </div>

        {/* EQ bars */}
        <div style={{
          marginTop: 26, display: 'flex', gap: 4, alignItems: 'flex-end', height: 32,
          opacity: stage >= 2 ? 1 : 0, transition: 'opacity 1s ease',
        }}>
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} style={{
              width: 4, height: '100%',
              background: `linear-gradient(to top, ${P.accent}, ${i % 3 === 0 ? P.accent3 : P.accent2})`,
              borderRadius: 2,
              transformOrigin: 'bottom',
              animation: `eq-bar ${0.5 + (i % 5) * 0.15}s ease-in-out ${i * 0.06}s infinite alternate`,
            }} />
          ))}
        </div>

        {/* CTA */}
        <button onClick={onEnter} style={{
          marginTop: 30,
          opacity: stage >= 3 ? 1 : 0, transition: 'opacity 0.8s ease',
          padding: '14px 36px', borderRadius: 100,
          background: P.accent, color: P.bg,
          border: 'none', fontFamily: 'var(--mono)', fontSize: 11,
          letterSpacing: '0.3em', fontWeight: 700, cursor: 'pointer',
          boxShadow: `0 0 30px ${hexToRgba(P.accent, 0.6)}`,
        }}>
          STEP INSIDE →
        </button>
      </div>

      {/* bottom ticker */}
      <div style={{
        borderTop: `1px solid ${hexToRgba(P.accent, 0.2)}`, background: hexToRgba(P.bg, 0.5),
        padding: '10px 0 28px', overflow: 'hidden',
        opacity: stage >= 2 ? 1 : 0, transition: 'opacity 1s ease',
      }}>
        <div style={{
          display: 'flex', whiteSpace: 'nowrap', gap: 32,
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.2em',
          color: P.mono,
          animation: 'ticker-splash 22s linear infinite',
        }}>
          {Array(2).fill(0).map((_, k) => (
            <React.Fragment key={k}>
              <span>✦ 5 PRIVATE KARAOKE ROOMS</span>
              <span>·</span>
              <span>HAPPY HOUR 5:30–7</span>
              <span>·</span>
              <span>HEATED TERRACE</span>
              <span>·</span>
              <span>COCKTAIL CLASSES £50PP</span>
              <span>·</span>
              <span>111 LONG LANE SE1</span>
              <span>·</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

window.Splash = Splash;
window.DEFAULT_PALETTE = DEFAULT_PALETTE;
